import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { useDictionary } from '../../../../../../utilities/hooks/UseDictionary';
import ImageHelper from '../../../../../../utilities/helpers/ImageHelper';
import placeholder from '../../../../../../assets/images/bg_placeholder_square.svg';

const MAX_PHOTOS = 5;

// Step 3 of the recommendation wizard: up to 5 photos (images only).
// Existing assets (edit) are removed via onDeleteExistingAsset; new files are
// collected locally and uploaded after the recommendation is saved.
const RecommendationPhotos = ({
	existingAssets,
	filesToUpload,
	setFilesToUpload,
	onDeleteExistingAsset
}) => {
	const dictionary = useDictionary();

	const total = existingAssets.length + filesToUpload.length;
	const remaining = MAX_PHOTOS - total;

	const onDrop = useCallback(
		(acceptedFiles) => {
			const room = MAX_PHOTOS - (existingAssets.length + filesToUpload.length);
			const next = acceptedFiles.slice(0, Math.max(room, 0)).map((file) =>
				Object.assign(file, { preview: URL.createObjectURL(file) })
			);
			setFilesToUpload([...filesToUpload, ...next]);
		},
		[existingAssets.length, filesToUpload, setFilesToUpload]
	);

	const { getRootProps, getInputProps } = useDropzone({
		accept: ['image/png', 'image/jpeg', 'image/jpg'],
		onDrop,
		disabled: remaining <= 0
	});

	const removePendingFile = (index) => {
		const next = [...filesToUpload];
		URL.revokeObjectURL(next[index]?.preview);
		next.splice(index, 1);
		setFilesToUpload(next);
	};

	return (
		<div className="new-experience-form-wrapper">
			<div className="new-experience-form-container thin-scrollbar-trans">
				<h2>{dictionary.recommendation_step_photos}</h2>
				<div style={{ marginTop: '8px' }} className="text">
					{dictionary.recommendation_photos_limit}
				</div>

				<div
					className="upload-files-container"
					style={{ marginTop: '16px' }}
				>
					{existingAssets.map((asset) => (
						<div key={asset.id} className="media-file-wrapper">
							<div className="media-file-container uploaded-file-container">
								<div
									className="uploaded-file-delete-icon"
									onClick={() => onDeleteExistingAsset(asset)}
								/>
								<img
									onError={(e) => {
										e.currentTarget.src = placeholder;
									}}
									className="media-file-bg"
									src={ImageHelper.getFullImageUrl(
										asset.thumbnail_url || asset.file_url
									)}
									alt=""
								/>
							</div>
						</div>
					))}
					{filesToUpload.map((file, index) => (
						<div key={file.preview} className="media-file-wrapper">
							<div className="media-file-container uploaded-file-container">
								<div
									className="uploaded-file-delete-icon"
									onClick={() => removePendingFile(index)}
								/>
								<img className="media-file-bg" src={file.preview} alt="" />
							</div>
						</div>
					))}
				</div>

				{remaining > 0 ? (
					<div
						className="upload-media-wrapper"
						style={{ marginTop: '16px' }}
						{...getRootProps()}
					>
						<input {...getInputProps()} className="gallery-upload-input" />
						<div className="upload-media-container">
							<div className="upload-gallery-icon" />
							<h3 style={{ fontSize: '18px', marginTop: '25px' }}>
								{dictionary.recommendation_step_photos}
							</h3>
							<div
								style={{ marginTop: '25px' }}
								className="secondary-button"
							>
								{dictionary.upload_from_device}
							</div>
						</div>
					</div>
				) : null}
			</div>
		</div>
	);
};

RecommendationPhotos.propTypes = {
	existingAssets: PropTypes.array.isRequired,
	filesToUpload: PropTypes.array.isRequired,
	setFilesToUpload: PropTypes.func.isRequired,
	onDeleteExistingAsset: PropTypes.func.isRequired
};

export default RecommendationPhotos;
